import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';

import type { Observable } from 'rxjs';
import { catchError, combineLatest, distinctUntilChanged, map, of, shareReplay, switchMap, take, tap } from 'rxjs';

import { MenubarModule } from '@openng/optimus-ui/menubar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TieredMenuModule } from '@openng/optimus-ui/tieredmenu';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import type { MenuItem } from '@openng/optimus-ui/api';
import { MessageService } from '@openng/optimus-ui/api';
import { DrawerModule } from '@openng/optimus-ui/drawer';
import { ProgressSpinnerModule } from '@openng/optimus-ui/progressspinner';
import { drawerHeaderDesignToken, menuHeaderDesignToken, menubarHeaderDesignToken } from '@tmt/traton-theme';

import { Theme } from 'src/app/enums/theme.enum';
import { AuthService } from 'src/app/services/auth.service';
import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';
import { environment } from 'src/environments/environment';
import { ThemeService } from 'src/app/services/theme.service';
import { PreferencesService } from 'src/app/services/preferences.service';
import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { AuthenticationMutations } from 'src/app/graphql/mutations/authentication.mutations';
import { LaunchDarklyFlags } from 'src/environments/environment.interfaces';
import { NexusService } from 'src/app/services/nexus.service';
import { FunctionalGroup } from 'src/app/models/functional-group';
import { NotificationService } from 'src/app/services/notification.service';

/**
 * Main header of the application.
 */
@Component({
	selector: 'app-header',
	imports: [MenubarModule, RouterLink, ButtonModule, AsyncPipe, ProgressSpinnerModule, ToggleSwitchModule, FormsModule, TieredMenuModule, DrawerModule, NgClass],
	templateUrl: './header.component.html',
	styleUrl: './header.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent {
	/** To check user data. */
	public authService = inject(AuthService);

	/** To get user admin status. */
	private launchDarklyService = inject(LaunchDarklyService);

	/** To toggle theme. */
	public themeService = inject(ThemeService);

	/** To handle user client preferences. */
	public preferencesService = inject(PreferencesService);

	private apolloClientSerivce = inject(ApolloClientService);

	private messageService = inject(MessageService);

	private notificationService = inject(NotificationService);

	private nexusService = inject(NexusService);

	private breakpointObserver = inject(BreakpointObserver);

	private router = inject(Router);

	public Theme = Theme;

	public title = 'Chimera';

	public homeUrl = environment.homeUrl;

	public menubarHeaderDesignToken = menubarHeaderDesignToken;

	public menuHeaderDesignToken = menuHeaderDesignToken;

	public drawerHeaderDesignToken = drawerHeaderDesignToken;

	public isDevelopment = !environment.production;

	public isSigningOut: boolean = false;

	public showSettings: boolean = false;

	public showUserDetails: boolean = false;

	public showPrivacyNotice: boolean = false;

	public isStaging: boolean = false;

	public breakpoint = `${environment.breakpoint.toString()}px`;

	public isMobile$ = this.breakpointObserver.observe([`(max-width: ${this.breakpoint})`]).pipe(
		map(result => result.matches),
		distinctUntilChanged(),
	);

	/**
	 * User settings
	 */
	public preferences$ = this.preferencesService.preferences$.pipe(
		tap(preferences => {
			this.isStaging = preferences.staging;
		}),
		shareReplay(1),
	);

	/**
	 * Staging setting only available if feature flag is enabled
	 */
	public stagingAvailable$ = this.launchDarklyService.launchDarklyFlags$.pipe(
		map(flags => flags[LaunchDarklyFlags.Staging]),
		shareReplay(1),
	);

	/**
	 * Observable of current user environment preferences.
	 * Provides true if staging is available and selected by user.
	 */
	public isStaging$ = combineLatest({
		prefersStaging: this.preferences$.pipe(map(preferences => preferences.staging)),
		stagingAvailable: this.stagingAvailable$,
	}).pipe(map(source => source.prefersStaging && source.stagingAvailable));

	/**
	 * Get users functional groups
	 */
	public functionalGroups$: Observable<FunctionalGroup[]> = this.authService.userData$.pipe(
		switchMap(userData => {
			if (!userData.sessionID) return of();
			return this.nexusService.allFunctionalGroups$.pipe(
				map(functionalGroups => {
					return functionalGroups.filter(functionalGroup => userData.functionalGroups.find(userDataFunctionalGroup => userDataFunctionalGroup === functionalGroup.uid));
				}),
			);
		}),
		shareReplay(1),
	);

	/**
	 * Sign out user
	 */
	public signOut() {
		this.isSigningOut = true;

		this.apolloClientSerivce.authLayerClient
			.pipe(
				take(1),
				switchMap(client =>
					client.mutate<{ signOut: boolean | null }>({
						mutation: AuthenticationMutations.signOut,
					}),
				),
				map(response => response ?? false),
				tap(response => {
					if (response) {
						this.messageService.add({ severity: 'success', life: 2000, summary: 'Successfully signed out' });
						this.authService.clearSession();
						this.isSigningOut = false;
					} else {
						this.isSigningOut = false;
						throw new Error('Failed when signing out');
					}
				}),
				catchError((error: unknown) => {
					this.isSigningOut = false;
					this.notificationService.notifyError('Error when signing out', error);
					return of(undefined);
				}),
				take(1),
			)
			.subscribe();
	}

	/**
	 * Top-level navigation between the app's feature areas.
	 */
	public navigationItems$: Observable<MenuItem[]> = of([
		{
			label: 'ECU Coverage',
			icon: 'pi pi-eye',
			routerLink: '/',
			routerLinkActiveOptions: { exact: true },
		},
		{
			label: 'Vehicle Test Suite',
			icon: 'pi pi-wrench',
			routerLink: '/vehicle-test-suite',
		},
		{
			label: 'CVIT Reporting',
			icon: 'pi pi-chart-bar',
			disabled: true,
		},
	]);

	/**
	 * Menu items in desktop view
	 * No internal navigation
	 */
	public menuItems$: Observable<MenuItem[]> = combineLatest({ theme: this.themeService.theme$, user: this.authService.userData$, preferences: this.preferences$ }).pipe(
		map(source => [
			{
				label: source.user.firstName + ' ' + source.user.lastName,
				icon: 'pi pi-user',
				visible: source.user.sessionID ? true : false,
				command: () => {
					this.showUserDetails = true;
				},
			},
			{
				label: 'Settings',
				icon: 'pi pi-cog',
				command: () => {
					this.showSettings = true;
				},
			},
			{
				label: 'Jira Configuration',
				icon: 'pi pi-cog',
				command: () => {
					void this.router.navigateByUrl('/settings');
				},
			},
			{
				icon: 'pi pi-question-circle',
				label: 'Support',
				command: () => {
					window.open(environment.supportUrl, '_blank');
				},
			},
			{
				icon: 'pi pi-book',
				label: 'Wiki',
				command: () => {
					window.open(environment.wikiUrl, '_blank');
				},
			},
			{
				icon: 'pi pi-home',
				label: 'Test Tools',
				command: () => {
					window.open(environment.homeUrl, '_blank');
				},
			},
			{
				label: source.user.sessionID ? 'Sign out' : 'Sign in',
				icon: source.user.sessionID ? 'pi pi-sign-out' : 'pi pi-sign-in',
				command: () => {
					if (source.user.sessionID) {
						this.signOut();
					} else {
						this.authService.login();
					}
				},
			},
		]),
	);

	/**
	 * Menu items in mobile view
	 * Showing both navigation and menu items
	 */
	public menuAndNavigationItems$ = combineLatest({ isMobile: this.isMobile$, navigationItems: this.navigationItems$, menuItems: this.menuItems$ }).pipe(
		map(source => (source.isMobile ? [...source.navigationItems, { separator: true }, ...source.menuItems] : source.navigationItems)),
	);
}
